import {
    Box,
    Table,
    TableBody,
    TableHead,
    TableContainer,
    TableCell,
    Pagination,
    TableRow,
    Paper,
    Typography,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
} from "@mui/material";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FlagIcon from '@mui/icons-material/Flag';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useDataViews } from '../context/DataViewsContext';
import { useMaintenanceView } from '../context/MaintenanceViewContext'; // Import MaintenanceViewContext
import { getGenericMaintenanceView, getGenericMaintenanceViewReport } from '../services/CatalogsService';
import { getSapOfflineStatus, updateSapOfflineStatus } from '../services/CatalogsService';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { MenuItem, Select, FormControl, InputLabel } from '@mui/material';

const TableContentMaintenance = () => {
    // Confirmation dialog for SAP status change
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingSapStatus, setPendingSapStatus] = useState(null);
    // SAP Offline state
    const [isSapOffline, setIsSapOffline] = useState(null); // null: loading, true/false: state
    const [sapOfflineId, setSapOfflineId] = useState(null);
    const [isUpdatingSapOffline, setIsUpdatingSapOffline] = useState(false);
    const [sapOfflineError, setSapOfflineError] = useState(null);
    // Fetch SAP Offline status on mount
    useEffect(() => {
        const fetchSapOffline = async () => {
            setSapOfflineError(null);
            try {
                const token = localStorage.getItem('site') || '';
                const response = await getSapOfflineStatus(token);
                setIsSapOffline(response?.data?.content?.value === 'true');
                setSapOfflineId(response?.data?.content?.id);
            } catch (err) {
                setSapOfflineError('Error loading SAP Offline status');
                setIsSapOffline(null);
            }
        };
        fetchSapOffline();
    }, []);
    // Update SAP Offline status
    const handleUpdateSapOffline = async (newValue) => {
        if (!sapOfflineId) return;
        setIsUpdatingSapOffline(true);
        setSapOfflineError(null);
        try {
            const token = localStorage.getItem('site') || '';
            await updateSapOfflineStatus(token, sapOfflineId, newValue);
            setIsSapOffline(newValue);
        } catch (err) {
            setSapOfflineError('Error updating SAP Offline status');
        } finally {
            setIsUpdatingSapOffline(false);
        }
    };
    const [page, setPage] = useState(0);
    const [rowsPerPage] = useState(10);
    const { dataViews, loading: loadingViews, error: errorViews } = useDataViews();
    const [viewConfig, setViewConfig] = useState(null);
    const [apiData, setApiData] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [filterCriteria, setFilterCriteria] = useState({});
    const [filterPersonalized, setFilterPersonalized] = useState('');
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedText, setSelectedText] = useState('');
    const [selectedColumn, setSelectedColumn] = useState('');
    const [hasSearched, setHasSearched] = useState(false);
    const [exportSeparator, setExportSeparator] = useState(';');
    const [lastSearch, setLastSearch] = useState({ filterCriteria: {}, filterPersonalized: '' });
    const [isSearchEnabled, setIsSearchEnabled] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [exportTimeout, setExportTimeout] = useState(false);

    const { selectedDataSource } = useMaintenanceView();
    // loading depende solo del contexto global

    // Determine which columns to use based on selectedDataSource
    const currentColumns = useMemo(() => {
        if (!viewConfig) return [];
        const config = viewConfig.views.find(v => v.name === selectedDataSource);
        return config?.columns || [];
    }, [selectedDataSource, viewConfig]);



    // Pagination logic
    const handlePageChange = useCallback(async (event, newPage) => {
        const newPageIndex = newPage - 1; // Material-UI Pagination is 1-based
        setPage(newPageIndex);
        
        // Si estamos usando datos de la API, recargar con la nueva página
        if (apiData.length > 0) {
            setIsLoadingData(true);
            setError(null);
            
            try {
                const token = localStorage.getItem('site') || '';
                const fields = currentColumns.map(col => col.id).join(',');
                
                const response = await getGenericMaintenanceView(
                    token,
                    selectedDataSource,
                    newPageIndex * rowsPerPage,
                    rowsPerPage,
                    fields,
                    filterCriteria,
                    filterPersonalized
                );
                
                setApiData(response.data.content || []);
                setTotalElements(response.data.total || 0);
                
                // Si total es 0, asegurar que la tabla esté completamente vacía
                if (response.data.total === 0) {
                    setApiData([]);
                    setTotalElements(0);
                }
            } catch (err) {
                console.error('Error loading API data for page:', err);
                // Only show error if it's not a "no data" case
                if (err.message && !err.message.includes('404') && !err.message.includes('No data')) {
                    setError('Error loading page data');
                } else {
                    setError(null);
                }
            } finally {
                setIsLoadingData(false);
            }
        }
    }, [apiData.length, selectedDataSource, rowsPerPage, filterCriteria, filterPersonalized, currentColumns]);

    useEffect(() => {
        // Cuando cambian las vistas globales, actualiza la config local
        if (dataViews && Array.isArray(dataViews)) {
            setViewConfig({ views: dataViews });
        }
    }, [dataViews]);
    // El loading ahora depende del contexto
    const loading = loadingViews;



    // Función para manejar cambios en filtros específicos
    const handleFilterChange = useCallback((fieldId, value) => {
        setFilterCriteria(prev => ({
            ...prev,
            [fieldId]: value
        }));
    }, []);

    // Función para manejar cambios en filtro personalizado
    const handlePersonalizedFilterChange = useCallback((value) => {
        setFilterPersonalized(value);
    }, []);

    // Función para cargar datos iniciales sin filtros
    const handleLoadInitialData = useCallback(async () => {
        if (!selectedDataSource || !viewConfig) return;
        
        setIsLoadingData(true);
        setError(null);
        
        try {
            const token = localStorage.getItem('site') || '';
            const fields = currentColumns.map(col => col.id).join(',');
            
            console.log('Loading initial data without filters:', {
                viewName: selectedDataSource,
                offset: 0,
                pageSize: rowsPerPage,
                fields
            });
            
            const response = await getGenericMaintenanceView(
                token,
                selectedDataSource,
                0,
                rowsPerPage,
                fields,
                {}, // Sin filtros
                '' // Sin filtro personalizado
            );
            
            console.log('Initial API response:', response);
            
            setApiData(response.data.content || []);
            setTotalElements(response.data.total || 0);
            setHasSearched(true); // Marcar que se ha hecho una búsqueda inicial
            
            // Si total es 0, asegurar que la tabla esté completamente vacía
            if (response.data.total === 0) {
                setApiData([]);
                setTotalElements(0);
            }
            
            setError(null);
        } catch (err) {
            console.error('Error loading initial data:', err);
            if (err.message && !err.message.includes('404') && !err.message.includes('No data')) {
                setError('Error loading initial data');
            } else {
                setError(null);
            }
            setApiData([]);
            setTotalElements(0);
        } finally {
            setIsLoadingData(false);
        }
    }, [selectedDataSource, viewConfig, currentColumns, rowsPerPage]);

    // Función para limpiar todos los filtros
    const handleClearFilters = useCallback(() => {
        console.log('Clearing all filters...');
        setFilterCriteria({});
        setFilterPersonalized('');
        setApiData([]);
        setTotalElements(0);
        setPage(0);
        setError(null);
        setHasSearched(false);
        // Recargar datos iniciales después de limpiar
        handleLoadInitialData();
    }, [handleLoadInitialData]);

    // Función para formatear texto largo
    const formatLongText = useCallback((text, columnId) => {
        if (!text) return '';
        
        const isLongColumn = columnId === 'RECEIVEDSTRING' || columnId === 'RESPONSESTRING';
        
        if (isLongColumn && text.length > 80) {
            // Truncar y mostrar los primeros 80 caracteres
            const truncated = text.substring(0, 80) + '...';
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" sx={{ flex: 1, fontSize: '0.7rem' }}>
                        {truncated}
                    </Typography>
                    <Tooltip title="See full text">
                        <IconButton 
                            size="small" 
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedText(text);
                                setSelectedColumn(columnId);
                                setModalOpen(true);
                            }}
                        >
                            <VisibilityIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            );
        }
        
        // Para texto corto, mostrar completo pero con estilo compacto
        if (isLongColumn) {
            return (
                <Typography variant="body2" sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}>
                    {text}
                </Typography>
            );
        }
        
        return text;
    }, []);

    // Función para copiar texto al portapapeles
    const copyToClipboard = useCallback(async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            // Aquí podrías mostrar una notificación de éxito
        } catch (err) {
            console.error('Error copying to clipboard:', err);
        }
    }, []);

    // Función para aplicar filtros
    const handleApplyFilters = useCallback(async () => {
        if (!selectedDataSource) return;
        
        setPage(0); // Resetear a la primera página
        setIsLoadingData(true);
        setError(null);
        setApiData([]); // Limpiar datos anteriores inmediatamente
        setTotalElements(0); // Limpiar total anterior
        setHasSearched(true); // Marcar que se ha hecho una búsqueda
        setLastSearch({ filterCriteria, filterPersonalized }); // Guardar filtros actuales
        
        try {
            // Obtener token del localStorage (asumiendo que se guarda ahí)
            const token = localStorage.getItem('site') || '';
            
            // Construir campos basados en las columnas configuradas
            const fields = currentColumns.map(col => col.id).join(',');
            
            console.log('Loading API data with params:', {
                viewName: selectedDataSource,
                offset: 0, // Siempre empezar desde 0 al aplicar filtros
                pageSize: rowsPerPage,
                fields,
                filterCriteria,
                filterPersonalized
            });
            
            const response = await getGenericMaintenanceView(
                token,
                selectedDataSource,
                0, // Siempre empezar desde 0 al aplicar filtros
                rowsPerPage,
                fields,
                filterCriteria,
                filterPersonalized
            );
            
            console.log('API response:', response);
            
            // El servidor devuelve la estructura directamente
            // Limpiar datos anteriores y establecer los nuevos
            setApiData(response.data.content || []);
            setTotalElements(response.data.total || 0);
            
            // Si total es 0, asegurar que la tabla esté completamente vacía
            if (response.data.total === 0) {
                setApiData([]);
                setTotalElements(0);
            }
            
            setError(null); // Limpiar errores previos
        } catch (err) {
            console.error('Error loading API data:', err);
            // Only show error if it's not a "no data" case
            if (err.message && !err.message.includes('404') && !err.message.includes('No data')) {
                setError('Error loading data from the API');
            } else {
                setError(null);
            }
            setApiData([]);
            setTotalElements(0);
        } finally {
            setIsLoadingData(false);
        }
    }, [selectedDataSource, rowsPerPage, filterCriteria, filterPersonalized, currentColumns]);

    // Función de exportación
    const handleExport = async () => {
        if (!selectedDataSource) return;
        setIsExporting(true);
        setExportTimeout(false);
        const timeoutId = setTimeout(() => setExportTimeout(true), 30000);
        try {
            const token = localStorage.getItem('site') || '';
            const fields = currentColumns.map(col => col.id).join(',');
            const requestBody = {
                viewName: selectedDataSource,
                fields,
                separator: exportSeparator
            };
            if (Object.keys(filterCriteria).length > 0) {
                requestBody.filterCriteria = filterCriteria;
            }
            if (filterPersonalized) {
                requestBody.filterPersonalized = filterPersonalized;
            }
            const { blob, extension } = await getGenericMaintenanceViewReport(token, requestBody);
            const urlBlob = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = urlBlob;
            a.download = `export_${selectedDataSource}.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(urlBlob);
        } catch (err) {
            console.error('[EXPORT] ERROR:', err);
            if (err.message === 'Failed to fetch') {
                alert('The export was interrupted or the server/proxy cut the connection before finishing. Try with fewer filters or contact the administrator.\n\nDetails: ' + err);
            } else {
                alert('Error during export: ' + err.message);
            }
        } finally {
            clearTimeout(timeoutId);
            setIsExporting(false);
        }
    };

    useEffect(() => {
        console.log('selectedDataSource:', selectedDataSource);
        // Cargar datos de la API cuando cambie la vista
        if (selectedDataSource && viewConfig) {
            // Resetear datos de API cuando cambie la vista
            setApiData([]);
            setTotalElements(0);
            setFilterCriteria({});
            setFilterPersonalized('');
            setPage(0);
            setError(null); // Limpiar errores previos
            setHasSearched(false); // Resetear la bandera de búsqueda
            
            // Cargar datos iniciales sin filtros
            handleLoadInitialData();
        }
    }, [selectedDataSource, viewConfig]);

    // Detectar cambios en filtros
    useEffect(() => {
        const filtersChanged =
            JSON.stringify(filterCriteria) !== JSON.stringify(lastSearch.filterCriteria) ||
            filterPersonalized !== lastSearch.filterPersonalized;
        setIsSearchEnabled(filtersChanged);
    }, [filterCriteria, filterPersonalized, lastSearch]);

    // Determine which data and columns to use based on selectedDataSource
    const currentData = useMemo(() => {
        // Si tenemos datos de la API, usarlos
        if (apiData.length > 0) {
            return apiData;
        }
        // Si se ha hecho una búsqueda pero no hay datos, retornar array vacío
        if (hasSearched) {
            return [];
        }
        // Solo mostrar datos mock si no se ha hecho ninguna búsqueda y no hay datos de API
        if (!viewConfig) return [];
        const config = viewConfig.views.find(v => v.name === selectedDataSource);
        return config?.data || [];
    }, [selectedDataSource, viewConfig, apiData, hasSearched]);

    // No sorting needed, use data as is
    const sortedData = useMemo(() => {
        return currentData;
    }, [currentData]);

    // Apply pagination
    const paginatedData = useMemo(() => {
        // Si no hay datos, retornar array vacío
        if (currentData.length === 0) {
            return [];
        }
        // Si tenemos datos de la API, ya vienen paginados
        if (apiData.length > 0) {
            return sortedData;
        }
        // Si no, aplicar paginación local
        const startIndex = page * rowsPerPage;
        return sortedData.slice(startIndex, startIndex + rowsPerPage);
    }, [sortedData, page, rowsPerPage, apiData, currentData.length]);

    const pageCount = useMemo(() => {
        // Si no hay datos, retornar 0
        if (currentData.length === 0) {
            return 0;
        }
        // Si tenemos datos de la API, usar el total de elementos de la API
        if (apiData.length > 0) {
            return Math.ceil(totalElements / rowsPerPage);
        }
        // Si no, calcular basado en datos locales
        return Math.ceil(sortedData.length / rowsPerPage);
    }, [sortedData, rowsPerPage, apiData, totalElements, currentData.length]);

    return (
        <Box component="section" sx={{ margin: 5 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '10px' }}>
                <Typography variant="h4" sx={{ textAlign: 'center', width: '100%' }} color="primary">
                    Maintenance View{selectedDataSource ? ` - ${selectedDataSource}` : ''}
                </Typography>
                {selectedDataSource === 'HDK_LOG' && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                        {isSapOffline === null ? (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.9rem' }}>Checking SAP status...</Typography>
                        ) : (
                            <Tooltip title={isSapOffline ? 'SAP Offline (Active)' : 'SAP Online (Normal)'}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <FiberManualRecordIcon sx={{ color: isSapOffline ? 'red' : 'green', fontSize: 18 }} />
                                    <Typography variant="body2" sx={{ fontSize: '0.9rem', color: isSapOffline ? 'red' : 'green', fontWeight: 500 }}>
                                        {isSapOffline ? 'SAP Offline' : 'SAP Online'}
                                    </Typography>
                                </Box>
                            </Tooltip>
                        )}
                        <Button
                            variant="contained"
                            color={isSapOffline ? "error" : "success"}
                            startIcon={<FlagIcon />}
                            size="small"
                            onClick={() => { setPendingSapStatus(!isSapOffline); setConfirmOpen(true); }}
                            disabled={isSapOffline === null || isUpdatingSapOffline || !sapOfflineId}
                            sx={{ ml: 1, textTransform: 'none', fontWeight: 500 }}
                        >
                            Change SAP Status
                        </Button>
                    </Box>
                )}
            </Box>
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirm SAP Status Change</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to change SAP status to <b>{pendingSapStatus ? 'Offline' : 'Online'}</b>?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={async () => { setConfirmOpen(false); await handleUpdateSapOffline(pendingSapStatus); }} color={pendingSapStatus ? 'error' : 'success'} variant="contained">Confirm</Button>
                </DialogActions>
            </Dialog>
            {sapOfflineError && (
                <Alert severity="error" sx={{ mb: 2 }}>{sapOfflineError}</Alert>
            )}
            {!hasSearched && !isLoadingData && !error && (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    Loading initial data...
                </Alert>
            )}
            
            {hasSearched && apiData.length === 0 && !isLoadingData && !error && (
                <Alert severity="info" sx={{ marginBottom: 2 }}>
                    {(filterCriteria && Object.keys(filterCriteria).some(key => filterCriteria[key])) || filterPersonalized ? 
                        'No records were found with the applied filters' : 
                        'No records available'
                    }
                </Alert>
            )}

            {loading ? (
                <Typography>Loading configuration...</Typography>
            ) : !viewConfig ? (
                <Typography color="error">Could not load configuration.</Typography>
            ) : !viewConfig.views.find(v => v.name === selectedDataSource) ? (
                <Typography color="error">View not found for "{selectedDataSource}".</Typography>
            ) : (
                <>
                    {viewConfig?.views.find(v => v.name === selectedDataSource)?.filters && (
                        <Accordion sx={{ marginBottom: 2 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Filters</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                    {viewConfig?.views.find(v => v.name === selectedDataSource)?.columns.map(column => (
                                        <TextField
                                            key={column.id}
                                            label={column.label}
                                            variant="outlined"
                                            size="small"
                                            value={filterCriteria[column.id] || ''}
                                            onChange={(e) => handleFilterChange(column.id, e.target.value)}
                                            sx={{ minWidth: column.type === 'date' ? '180px' : '150px', flex: '1 1 150px' }}
                                            type={column.type === 'date' ? 'date' : 'text'}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={column.type === 'date' ? {
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <span role="img" aria-label="calendar">📅</span>
                                                    </InputAdornment>
                                                )
                                            } : undefined}
                                        />
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}
                    <Accordion sx={{ marginBottom: 2 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Custom Filter</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box>
                                <TextField
                                    label="Custom filter"
                                    multiline
                                    placeholder="ORDERNO='a' and LOGID=378648"
                                    rows={4}
                                    variant="outlined"
                                    value={filterPersonalized}
                                    onChange={(e) => handlePersonalizedFilterChange(e.target.value)}
                                    sx={{ width: '100%' }}
                                />
                                
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2, gap: 2 }}>
                        {/* Botones de búsqueda a la izquierda */}
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button 
                                variant="contained" 
                                color="primary" 
                                sx={{ width: '120px' }}
                                onClick={handleApplyFilters}
                                disabled={!isSearchEnabled || isLoadingData}
                            >
                                {isLoadingData ? <CircularProgress size={20} color="inherit" /> : 'SEARCH'}
                            </Button>
                            <Button 
                                variant="contained" 
                                color="error" 
                                sx={{ width: '120px' }}
                                onClick={handleClearFilters}
                                disabled={isLoadingData}
                            >
                                CLEAR
                            </Button>
                        </Box>
                        {/* Select y export a la derecha */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <FormControl size="small" sx={{ minWidth: 100 }}>
                                <InputLabel id="export-separator-label">Separator</InputLabel>
                                <Select
                                    labelId="export-separator-label"
                                    id="export-separator"
                                    value={exportSeparator}
                                    label="Separator"
                                    onChange={e => setExportSeparator(e.target.value)}
                                >
                                    <MenuItem value={';'}>;</MenuItem>
                                    <MenuItem value={','}>,</MenuItem>
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                color="success"
                                startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
                                onClick={handleExport}
                                sx={{ minWidth: 120 }}
                                disabled={isSearchEnabled || isLoadingData || isExporting}
                            >
                                Export
                            </Button>
                        </Box>
                    </Box>
                    {console.log('Rendering buttons - BUSCAR and CLEAR')}


                    {error && (
                        <Alert severity="error" sx={{ marginBottom: 2 }}>
                            {error}
                        </Alert>
                    )}

                    <TableContainer component={Paper}>
                        <Table sx={{ minWidth: 650 }} aria-label="maintenance table">
                            <TableHead>
                                <TableRow>
                                    {currentColumns.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            align="center"
                                            sx={{ 
                                                width: column.width || 'auto', 
                                                maxWidth: column.width || 'auto',
                                                overflowWrap: 'break-word',
                                                wordBreak: 'break-all',
                                                fontSize: '0.75rem',
                                                padding: (column.id === 'RECEIVEDSTRING' || column.id === 'RESPONSESTRING') ? '8px 4px' : 'inherit',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {column.id}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoadingData ? (
                                    <TableRow>
                                        <TableCell colSpan={currentColumns.length} align="center">
                                            <CircularProgress />
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={currentColumns.length} align="center">
                                            <Typography variant="body2" color="text.secondary">
                                                {(filterCriteria && Object.keys(filterCriteria).some(key => filterCriteria[key])) || filterPersonalized ? 
                                                    'No records were found with the applied filters' : 
                                                    'No records available'
                                                }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedData.map((row, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                                                                    {currentColumns.map((column) => (
                                            <TableCell 
                                                key={column.id} 
                                                align="center" 
                                                sx={{ 
                                                    width: column.width || 'auto', 
                                                    maxWidth: column.width || 'auto',
                                                    overflowWrap: 'break-word',
                                                    wordBreak: 'break-all',
                                                    whiteSpace: 'pre-wrap',
                                                    fontSize: (column.id === 'RECEIVEDSTRING' || column.id === 'RESPONSESTRING') ? '0.75rem' : 'inherit',
                                                    lineHeight: (column.id === 'RECEIVEDSTRING' || column.id === 'RESPONSESTRING') ? '1.2' : 'inherit',
                                                    padding: (column.id === 'RECEIVEDSTRING' || column.id === 'RESPONSESTRING') ? '8px 4px' : 'inherit'
                                                }}
                                            >
                                                {formatLongText(row[column.id], column.id)}
                                            </TableCell>
                                        ))}
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Box display="flex" justifyContent="center" component="div" sx={{ marginTop: 5 }}>
                        <Pagination
                            count={pageCount}
                            page={page + 1}
                            onChange={handlePageChange}
                            color="primary"
                        />
                    </Box>
                </>
            )}

            {/* Modal para mostrar texto completo */}
            <Dialog 
                open={modalOpen} 
                onClose={() => setModalOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            {selectedColumn === 'RECEIVEDSTRING' ? 'Received String' : 
                             selectedColumn === 'RESPONSESTRING' ? 'Response String' : 
                             selectedColumn}
                        </Typography>
                        <IconButton 
                            onClick={() => copyToClipboard(selectedText)}
                            title="Copy to clipboard"
                        >
                            <ContentCopyIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ 
                        backgroundColor: '#f5f5f5', 
                        padding: 2, 
                        borderRadius: 1,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                        maxHeight: '400px',
                        overflow: 'auto'
                    }}>
                        {selectedColumn === 'RECEIVEDSTRING' || selectedColumn === 'RESPONSESTRING' ? (
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Formatted data:
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: 'white', 
                                    padding: 1, 
                                    borderRadius: 1,
                                    border: '1px solid #ddd',
                                    mb: 2
                                }}>
                                    {selectedText.split('&').map((param, index) => {
                                        const [key, value] = param.split('=');
                                        return (
                                            <Box key={index} sx={{ 
                                                display: 'flex', 
                                                justifyContent: 'space-between',
                                                padding: '2px 0',
                                                borderBottom: index < selectedText.split('&').length - 1 ? '1px solid #eee' : 'none'
                                            }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                                                    {key}:
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: '#666' }}>
                                                    {value || ''}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                    Original text:
                                </Typography>
                                <Box sx={{ 
                                    backgroundColor: 'white', 
                                    padding: 1, 
                                    borderRadius: 1,
                                    border: '1px solid #ddd'
                                }}>
                                    {selectedText}
                                </Box>
                            </Box>
                        ) : (
                            selectedText
                        )}
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setModalOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TableContentMaintenance;
